import { serviceItemListQuerySchema, type Page, type ServiceItemListQuery } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { toListParams } from '../../../../../shared/policies/list-params';
import { ApiError } from '../../../../../shared/shared.api';
import { listServiceItems, type ServiceItemView } from '../service-items.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type ServiceItemsState =
  { status: 'loading' } | { status: 'loaded'; page: Page<ServiceItemView>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: ServiceItemListQuery) => toListParams(query).toString();

function toErrorState(err: unknown): ServiceItemsState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of service items the URL's list query asks for, re-fetched whenever that query changes. */
export function useServiceItemList() {
  const list = useListQuery(serviceItemListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<ServiceItemsState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    listServiceItems(query)
      .then((page) => !cancelled && setState({ status: 'loaded', page, queryKey: keyOf(query) }))
      .catch((err) => !cancelled && setState(toErrorState(err)));

    return () => {
      cancelled = true;
    };
  }, [query]);

  /** Re-fetches the current page after a mutation commits. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', page: await listServiceItems(query), queryKey: keyOf(query) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, list, isRefreshing: state.status === 'loaded' && state.queryKey !== keyOf(query), reload };
}
